import React from 'react';

import { FormInputType, FormInputValidation } from '@candu-ai/forms/document-types';
import { useManageFormFieldValidation } from '@candu-ai/forms/editor-utils';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import Node from 'craft/components/Node';
import { BuiltInComponents } from 'craft/constants';
import { useEditorNode, useNodeContext } from 'craft/hooks';
import { FormFieldValidationMessage } from 'craft/resolvers/Forms/FormFieldValidationMessage';
import { useFormAncestor, useFormIdFromAncestor } from 'craft/state';
import { CssSelectorId, Resolver, ToolboxComponentSections } from 'craft/types';

import { FormLabel } from './FormLabel';
import { getFormFieldNodeRules } from './helpers';
import { TextInputToolbox } from './toolbox/TextInputToolbox';

interface Props {
  formInputId: string;
  formId: string;
  label: string;
  placeholder?: string;
  showLabel?: boolean;
  type?: FormInputType;
  validation?: FormInputValidation[];
}

export const FormTextInput: Resolver = ({
  formInputId,
  formId,
  label,
  placeholder,
  showLabel,
  type,
  validation,
}: Props) => {
  const { id } = useNodeContext();
  const { shouldDisplayErrorState } = useFormAncestor(id);
  const { setProps } = useEditorNode(id);

  useFormIdFromAncestor(formId);

  const {
    validationIndexMapping,
    isRequired,
    hasEmailValidation,
    onSetAtomicValidationProps,
  } = useManageFormFieldValidation({
    setProps,
    validation,
  });

  const inputClassName = toCssSelector('form', {
    output: 'html',
    className: 'inputField',
  });

  const classNames = `${inputClassName} ${shouldDisplayErrorState ? ' error' : ''}`.trim();

  return (
    <Node is="div" data-type="form-text-input">
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      {showLabel && <FormLabel id={id} required={isRequired} label={label} />}
      <input
        id={formInputId}
        type={type}
        className={classNames}
        readOnly
        placeholder={placeholder}
      />
      {isRequired && shouldDisplayErrorState && (
        <FormFieldValidationMessage
          nodeId={id}
          validationKey="required"
          onSetProps={(value) => onSetAtomicValidationProps(value, 'required')}
          message={validation?.[validationIndexMapping.required]?.message}
        />
      )}
      {hasEmailValidation && shouldDisplayErrorState && (
        <FormFieldValidationMessage
          nodeId={id}
          validationKey="email"
          onSetProps={(value) => onSetAtomicValidationProps(value, 'email')}
          message={validation?.[validationIndexMapping.email]?.message}
        />
      )}
    </Node>
  );
};

FormTextInput.craft = {
  displayName: 'Form: Text Input',
  name: BuiltInComponents.FormTextInput,
  isCanvas: false,
  rules: getFormFieldNodeRules(),
};

FormTextInput.toolbox = {
  component: [TextInputToolbox],
  schema: {
    [ToolboxComponentSections.Border]: {
      cssSelectorId: CssSelectorId.FormTextInput,
    },
    [ToolboxComponentSections.Shadow]: {
      cssSelectorId: CssSelectorId.FormTextInput,
    },
  },
};

FormTextInput.selectedToolbar = {};
